import React from "react";
import { Avatar, Badge, Btn, Card } from "../../../ForBrands/ui/BrandUI";
import { ListRule, MetaCols, MobileCard, MobileEmpty, TextLink } from "../../../ForBrands/ui/mobile/MobileUI";
import { usePhoneTheme } from "../../../ForBrands/useIsPhone";
import { lastContactLabel } from "./circleModel";

// My Circle contacts table (Figma 04 · My Circle): Contact, Role, Linked to,
// Last contact and the row action. Figma widths are 340/190/220/200/246 of
// 1196; the table scrolls sideways below 900px. Inside the Coach Mode phone
// theme (COACH MODE V2 mobile, 2412:1393) the same rows render as contact
// cards instead (CircleContactCards).

const COLUMNS = [
  { label: "Contact", width: "28.4%" },
  { label: "Role", width: "15.9%" },
  { label: "Linked to", width: "18.4%" },
  { label: "Last contact", width: "16.7%" },
  { label: "Action", width: "20.6%", hidden: true },
];

// Generic on purpose: an email can be withheld by the athlete's contact
// setting or by the private-data rule (a sign-up guardian).
const NOT_SHARED_HINT = "This person's email is not shared with you.";

// The 40px outline avatar; "??" for a guardian who is not linked yet.
const ContactAvatar = ({ row }) => (
  <Avatar
    src={row.avatar}
    name={row.name}
    initials={row.kind === "missing" ? "??" : undefined}
    size={40}
    tone="outline"
    className="!text-[12px] !font-semibold"
  />
);

// The contact's name. An athlete's name opens their profile.
const ContactName = ({ row, onOpenAthlete, className }) =>
  row.kind === "athlete" && onOpenAthlete ? (
    <button type="button" onClick={() => onOpenAthlete(row)} className={`${className} hover:underline`}>
      {row.name}
    </button>
  ) : (
    <span className={className}>{row.name}</span>
  );

// Avatar, name and meta line.
const ContactCell = ({ row, onOpenAthlete }) => (
  <span className="flex items-center gap-[10px]">
    <ContactAvatar row={row} />
    <span className="flex min-w-0 flex-col gap-[2px]">
      <ContactName row={row} onOpenAthlete={onOpenAthlete} className="truncate text-left bm-h-xs text-bm-ink" />
      {row.subtitle && <span className="truncate bm-body-s text-bm-muted">{row.subtitle}</span>}
    </span>
  </span>
);

const ActionCell = ({ row, busy, onAction }) =>
  row.action ? (
    <Btn variant="secondary" size="sm" onClick={() => onAction(row)} disabled={busy}>
      {busy ? "Opening..." : row.action}
    </Btn>
  ) : (
    <span className="bm-body-s text-bm-tertiary" title={NOT_SHARED_HINT}>
      Email not shared
    </span>
  );

const ContactRow = ({ row, busy, onAction, onOpenAthlete }) => {
  const last = lastContactLabel(row);
  return (
    <tr>
      <td className="bm-td">
        <ContactCell row={row} onOpenAthlete={onOpenAthlete} />
      </td>
      <td className="bm-td">
        <Badge tone="outline">{row.role}</Badge>
      </td>
      <td className="bm-td truncate bm-body-m text-bm-ink">{row.linkedTo}</td>
      <td className={`bm-td bm-body-m ${last.muted ? "text-bm-tertiary" : "text-bm-ink"}`}>{last.text}</td>
      <td className="bm-td text-right">
        <ActionCell row={row} busy={busy} onAction={onAction} />
      </td>
    </tr>
  );
};

const ContactsTable = ({ rows, busyId, onAction, onOpenAthlete, emptyText }) => (
  <Card padding="p-0" className="overflow-hidden">
    <div className="overflow-x-auto">
      <table className="w-full min-w-[900px] table-fixed [&_tr:last-child_td]:border-b-0">
        <colgroup>
          {COLUMNS.map((c) => (
            <col key={c.label} style={{ width: c.width }} />
          ))}
        </colgroup>
        <thead>
          <tr>
            {COLUMNS.map((c) => (
              <th key={c.label} className={`bm-th !h-11 ${c.hidden ? "text-right" : "text-left"}`}>
                {c.hidden ? <span className="sr-only">{c.label}</span> : c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td colSpan={COLUMNS.length} className="bm-td !py-10 text-center bm-body-m text-bm-muted">
                {emptyText}
              </td>
            </tr>
          )}
          {rows.map((row) => (
            <ContactRow key={row.id} row={row} busy={busyId === row.id} onAction={onAction} onOpenAthlete={onOpenAthlete} />
          ))}
        </tbody>
      </table>
    </div>
  </Card>
);

// ------------------------------------------------------------ Phone cards
// One card per contact (2412:1488): the avatar, the name (bm-h-xs) and its
// meta line, the role badge on the right; a rule; LINKED TO and LAST
// CONTACT side by side; then the action as an orange text link ("Message",
// "Request link", "Opening..." while it logs), or "Email not shared" with
// the same hint as the table. The link's tap area grows 8px up and down
// without moving the layout.
const ContactCard = ({ row, busy, onAction, onOpenAthlete }) => {
  const last = lastContactLabel(row);
  return (
    <MobileCard as="li">
      <div className="flex items-center gap-3">
        <ContactAvatar row={row} />
        <div className="flex min-w-0 flex-1 flex-col gap-[2px] break-words">
          <h2 className="bm-h-xs text-bm-ink">
            <ContactName row={row} onOpenAthlete={onOpenAthlete} className="text-left" />
          </h2>
          {row.subtitle && <p className="bm-body-s text-bm-muted">{row.subtitle}</p>}
        </div>
        <Badge tone="outline" className="shrink-0">
          {row.role}
        </Badge>
      </div>
      <ListRule />
      <MetaCols
        items={[
          { key: "linked", label: "Linked to", value: row.linkedTo, valueClass: "bm-body-s text-bm-ink" },
          { key: "last", label: "Last contact", value: last.text, valueClass: `bm-body-s ${last.muted ? "text-bm-tertiary" : "text-bm-ink"}` },
        ]}
      />
      {row.action ? (
        <TextLink onClick={() => onAction(row)} busy={busy} className="-my-2 self-start py-2">
          {busy ? "Opening..." : row.action}
        </TextLink>
      ) : (
        <p className="bm-body-s text-bm-tertiary" title={NOT_SHARED_HINT}>
          Email not shared
        </p>
      )}
    </MobileCard>
  );
};

// The phone list of contacts; the filter's empty line in a phone card when
// nobody is in view.
export const CircleContactCards = ({ rows, busyId, onAction, onOpenAthlete, emptyText }) =>
  rows.length === 0 ? (
    <MobileEmpty>{emptyText}</MobileEmpty>
  ) : (
    <ul aria-label="Contacts" className="flex flex-col gap-3">
      {rows.map((row) => (
        <ContactCard key={row.id} row={row} busy={busyId === row.id} onAction={onAction} onOpenAthlete={onOpenAthlete} />
      ))}
    </ul>
  );

// The table on tablets and desktops (and outside the phone theme), the
// contact cards on phones in Coach Mode. One tree renders, never both.
const CircleContactsTable = (props) => {
  const phone = usePhoneTheme();
  return phone ? <CircleContactCards {...props} /> : <ContactsTable {...props} />;
};

export default CircleContactsTable;
